import { Badge, Button, Group, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { AdminDiscount } from "@/features/discounts/api/types";
import { discountKindLabel } from "@/features/discounts/lib/kind-options";
import { ActiveBadge } from "@/shared/ui/active-badge";

function formatEndsOn(value: string): string {
	const [year, month, day] = value.split("-");
	return `${day}.${month}.${year}`;
}

export function OffersTable({
	rows,
	page,
	hasNextPage,
	onPageChange,
	onEdit,
	onDelete,
}: {
	rows: AdminDiscount[];
	page: number;
	hasNextPage: boolean;
	onPageChange: (page: number | ((value: number) => number)) => void;
	onEdit: (row: AdminDiscount) => void;
	onDelete: (row: AdminDiscount) => void;
}) {
	const { t } = useTranslation();
	return (
		<>
			<Table.ScrollContainer type="native" minWidth={1080}>
				<Table verticalSpacing="sm">
					<Table.Thead>
						<Table.Tr>
							<Table.Th>{t("discounts.titleField")}</Table.Th>
							<Table.Th miw={140}>{t("discounts.kind")}</Table.Th>
							<Table.Th miw={148} style={{ whiteSpace: "nowrap" }}>
								{t("common.active")}
							</Table.Th>
							<Table.Th>{t("discounts.promoCode")}</Table.Th>
							<Table.Th>{t("discounts.endsOn")}</Table.Th>
							<Table.Th>{t("discounts.bindings")}</Table.Th>
							<Table.Th />
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{rows.map((row) => (
							<Table.Tr key={row.id}>
								<Table.Td>{row.name}</Table.Td>
								<Table.Td miw={140} style={{ whiteSpace: "nowrap" }}>
									<Badge
										variant="light"
										tt="none"
										styles={{ label: { overflow: "visible" } }}
									>
										{discountKindLabel(row.type)}
									</Badge>
								</Table.Td>
								<Table.Td miw={148} style={{ whiteSpace: "nowrap" }}>
									<ActiveBadge active={row.isActive} />
								</Table.Td>
								<Table.Td>{row.promoCode ?? t("common.dash")}</Table.Td>
								<Table.Td>
									{row.endsOn
										? formatEndsOn(row.endsOn)
										: t("discounts.openEnded")}
								</Table.Td>
								<Table.Td>
									{(row.type === "CORPORATE" ? row.partners : row.categories)
										.map((item) => item.name)
										.join(", ") || t("common.dash")}
								</Table.Td>
								<Table.Td>
									<Group gap="xs" justify="flex-end">
										<Button
											size="xs"
											variant="light"
											onClick={() => onEdit(row)}
										>
											{t("common.edit")}
										</Button>
										<Button
											size="xs"
											color="red"
											variant="light"
											onClick={() => onDelete(row)}
										>
											{t("common.delete")}
										</Button>
									</Group>
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			</Table.ScrollContainer>
			<Group justify="flex-end" mt="md">
				<Button
					variant="default"
					disabled={page <= 1}
					onClick={() => onPageChange((value) => value - 1)}
				>
					{t("common.previous")}
				</Button>
				<Button
					variant="default"
					disabled={!hasNextPage}
					onClick={() => onPageChange((value) => value + 1)}
				>
					{t("common.next")}
				</Button>
			</Group>
		</>
	);
}
